import { QuoteRequest } from '../types';

export const calculatePrice = (request: QuoteRequest, materials: any[], finishingOptions: any[], volumeDiscountsOverride?: any[]): number => {
  const { product, width, height, quantity, material, finishing, colorSpec } = request;
  
  // Calculate square footage
  const squareFeet = (width * height) / 144; // Convert square inches to square feet
  
  // Check if this is a fixed size product
  let baseProductCost = 0;
  if (!product.customizable && product.fixedSizes && product.fixedSizes.length > 0) {
    // Find the matching fixed size
    const fixedSize = product.fixedSizes.find(size => size.width === width && size.height === height);
    if (fixedSize) {
      // Use the fixed price for this size
      baseProductCost = fixedSize.price;
    } else {
      // Fallback to calculation if size not found (shouldn't happen in normal use)
      baseProductCost = product.basePrice * squareFeet;
    }
  } else {
    // Customizable product - use base price calculation
    baseProductCost = product.basePrice * squareFeet;
  }
  
  // Get material pricing
  let materialCost = baseProductCost; // Start with base product cost
  
  // Only apply material pricing if materials exist and a material is selected
  if (materials && materials.length > 0 && material) {
    const materialOption = materials.find(m => m.id === material);
    if (materialOption) {
      if (materialOption.pricingType === 'multiplier') {
        // Percentage multiplier - applies to base product cost
        materialCost = baseProductCost * (materialOption.priceMultiplier || 1);
      } else if (materialOption.pricingType === 'fixed') {
        // Fixed cost per square foot - adds to base product cost
        materialCost = baseProductCost + (materialOption.fixedCost || 0) * squareFeet;
      } else {
        // Fallback for old data structure
        materialCost = baseProductCost * (materialOption.priceMultiplier || 1);
      }
    }
  }
  
  // Get finishing price (can be flat fee or per sq ft)
  const finishingOption = finishingOptions?.find(f => f.id === finishing);
  let finishingPrice = 0;
  if (finishingOption) {
    if (finishingOption.pricingType === 'perSqFt') {
      finishingPrice = (finishingOption.price || 0) * squareFeet;
    } else {
      // Default to flat fee for backward compatibility
      finishingPrice = finishingOption.price || 0;
    }
  }
  
  // Get color spec price (per sq ft)
  const colorSpecOption = product.colorSpecs?.find(c => c.id === colorSpec);
  const colorSpecPrice = colorSpecOption?.price || 0;
  
  // Calculate custom options cost
  let customOptionsCost = 0;
  if (request.customOptions && product.customOptions) {
    for (const [optionId, choiceId] of Object.entries(request.customOptions)) {
      const option = product.customOptions.find(opt => opt.id === optionId);
      if (option) {
        const choice = option.options.find(ch => ch.id === choiceId);
        if (choice) {
          if (choice.pricingType === 'multiplier' && choice.priceMultiplier) {
            // Multiplier applies to the material cost (base + material modifier)
            customOptionsCost += materialCost * (choice.priceMultiplier - 1);
          } else if (choice.pricingType === 'fixed' && choice.fixedCost) {
            // Fixed cost per square foot
            customOptionsCost += choice.fixedCost * squareFeet;
          } else if (choice.pricingType === 'flat' && choice.flatCost) {
            // Flat cost (one-time fee)
            customOptionsCost += choice.flatCost;
          }
        }
      }
    }
  }
  
  // Calculate unit price
  let unitPrice = materialCost + finishingPrice + colorSpecPrice * squareFeet + customOptionsCost;
  
  // Get volume discounts - use override if provided, otherwise no discounts
  const volumeDiscounts = volumeDiscountsOverride || [];
  
  // Apply quantity discounts only if product is eligible
  let discount = 0;
  if (product.eligibleForVolumeDiscount !== false) {
    for (const rule of volumeDiscounts.sort((a: any, b: any) => b.minQuantity - a.minQuantity)) {
      if (quantity >= rule.minQuantity) {
        discount = rule.discount;
        break;
      }
    }
  }
  
  const discountedPrice = unitPrice * (1 - discount);
  
  // Calculate total
  return Math.round(discountedPrice * quantity * 100) / 100;
};

export const generateItemId = (request: QuoteRequest): string => {
  const { product, width, height, material, finishing, colorSpec, customOptions } = request;
  let customOptionsString = '';
  if (customOptions) {
    // Sort the custom options by key to ensure consistent ID generation
    const sortedOptions = Object.entries(customOptions).sort(([a], [b]) => a.localeCompare(b));
    customOptionsString = '-' + sortedOptions.map(([optionId, choiceId]) => `${optionId}:${choiceId}`).join('-');
  }
  return `${product.id}-${width}x${height}-${material}-${finishing}-${colorSpec}${customOptionsString}-${Date.now()}`;
};